/**
 * Интерфейсы для описаний лабораторной работы
 */
interface DescriptionItem {
    title: string;
    content: string;
}

interface FormulaItem extends DescriptionItem {
    description: string;
}

/**
 * Описания процессов и формул для лабораторной работы
 */
export const LAB_DESCRIPTIONS = {
    title: "Лабораторная работа: Исследование зависимости сопротивления полупроводников от температуры",
    
    purpose: "Цель работы: исследовать зависимость сопротивления полупроводника от температуры и определить энергию активации примесей.",
    
    theory: [
        {
            title: "Полупроводники и их свойства",
            content: "Полупроводники - это материалы, электропроводность которых сильно зависит от температуры, освещения и примесей. При повышении температуры их сопротивление, в отличие от металлов, уменьшается. Это связано с увеличением концентрации носителей заряда из-за теплового возбуждения."
        },
        {
            title: "Температурная зависимость проводимости",
            content: "В области примесной проводимости зависимость концентрации носителей заряда от температуры определяется формулой:\nn ~ exp(-ΔE/2kT)\nгде ΔE - энергия активации примесей, k - постоянная Больцмана, T - абсолютная температура.\n\nПроводимость полупроводника G прямо пропорциональна концентрации носителей заряда, поэтому:\nln(G) = A - ΔE/2kT\nгде A - некоторая константа."
        },
        {
            title: "Определение энергии активации",
            content: "График зависимости ln(G) от 1/T представляет собой прямую линию. По наклону этой прямой можно определить энергию активации примесей:\nΔE = -2k * tg(α)\nгде tg(α) - тангенс угла наклона прямой к оси абсцисс, k - постоянная Больцмана."
        }
    ] as DescriptionItem[],
    
    procedure: [
        {
            title: "Подготовка к измерениям",
            content: "1. Ознакомиться с установкой и правилами техники безопасности\n2. Проверить подключение измерительных приборов\n3. Подготовить таблицу для записи результатов измерений"
        },
        {
            title: "Проведение измерений",
            content: "1. Измерить сопротивление образца при комнатной температуре\n2. Постепенно нагревать образец, измеряя его сопротивление через каждые 5-10°C\n3. Провести измерения в диапазоне температур от комнатной до 100°C\n4. Записать результаты в таблицу"
        },
        {
            title: "Обработка результатов",
            content: "1. Рассчитать для каждого измерения:\n   - температуру в Кельвинах\n   - обратную температуру 1/T\n   - проводимость G = 1/R\n   - натуральный логарифм проводимости ln(G)\n2. Построить график зависимости ln(G) от 1/T\n3. Определить наклон прямой\n4. Вычислить энергию активации примесей"
        }
    ] as DescriptionItem[],
    
    formulas: [
        {
            title: "Температура в Кельвинах",
            content: "T(K) = T(°C) + 273.15",
            description: "Перевод температуры из градусов Цельсия в Кельвины"
        },
        {
            title: "Проводимость",
            content: "G = 1/R",
            description: "Расчет проводимости как величины, обратной сопротивлению"
        },
        {
            title: "Натуральный логарифм проводимости",
            content: "ln(G) = ln(1/R)",
            description: "Логарифм проводимости для построения графика"
        },
        {
            title: "Энергия активации",
            content: "ΔE = -2k * (Δln(G)/Δ(1/T))",
            description: "k = 1.380649×10⁻²³ Дж/К - постоянная Больцмана\nΔln(G)/Δ(1/T) - тангенс угла наклона прямой на графике"
        }
    ] as FormulaItem[]
}; 